import { ErrorHandler, NgModule } from '@angular/core'
import Bugsnag from '@bugsnag/js'
import { BugsnagErrorHandler } from '@bugsnag/plugin-angular'
import { environment } from '@environment'


if (environment.production) {
  Bugsnag.start({
    apiKey: environment.bugsnag,
    onError: (event) => {
      const error = event.errors?.[0];
      const errorClass = error?.errorClass ?? '';
      const errorMsg = error?.errorMessage ?? '';
      const stacktrace = error?.stacktrace ?? [];
      const hasStackSource = (source: string) => stacktrace.some((frame: any) => frame?.file?.includes(source));

      // Google Identity Services internal error
      if ((errorMsg === 'na' || errorMsg === 'oa') && hasStackSource('accounts.google.com/gsi/client')) {
        return false;
      }

      // Lazy-loaded chunk errors
      if (errorClass === 'ChunkLoadError' || errorMsg.includes('ChunkLoadError') || errorMsg.includes('Loading chunk')) {
        return false;
      }

      // Invalid boolean Promise rejection
      if (errorMsg === 'Uncaught (in promise): true' || errorMsg.includes('Uncaught (in promise): true')) {
        return false;
      };

      // Promise rejected with null (no error object/stack) – thrown by third-party scripts
      // e.g. Google reCAPTCHA when its widget is removed from the DOM mid-load
      if (errorMsg === 'Uncaught (in promise): [object Null]' || errorMsg === 'Uncaught (in promise): null') {
        return false;
      }

      // navigator.getBattery() blocked by the browser (privacy) – called by third-party scripts, not our code
      if (errorClass === 'NotSupportedError' && errorMsg.includes('Battery Status API')) {
        return false;
      }

      // Benign browser notice: a ResizeObserver callback caused another resize in the same frame;
      // the browser just defers the notification to the next frame. No user impact.
      if (errorMsg.includes('ResizeObserver loop')) {
        return false;
      }

      // API errors
      if (errorClass === 'HttpErrorResponse') {
        // Facebook Graph API errors
        if (errorMsg.includes('graph.facebook.com')) {
          return false;
        }
      }

      // Google reCAPTCHA timeout
      if (errorMsg.includes('reCAPTCHA Timeout') && hasStackSource('gstatic.com/recaptcha')) {
        return false;
      }

      // Google reCAPTCHA Trusted Types callback fired after its frame/page was torn down
      if (errorMsg.includes("on 'CreateHTMLCallback'") && hasStackSource('gstatic.com/recaptcha')) {
        return false;
      }

      // Google reCAPTCHA styling its widget/challenge after the container was removed (navigation, dialog closed)
      // – only when reCAPTCHA is the top frame, so our own null-style bugs still report
      if (errorMsg.includes("(reading 'style')") && stacktrace[0]?.file?.includes('gstatic.com/recaptcha')) {
        return false;
      }

      // AdRoll pixel – its IndexedDB code breaks where IDB is unavailable (Safari private mode, some WebViews)
      if (errorMsg.includes('_adroll') && hasStackSource('s.adroll.com')) {
        return false;
      }

      // Microsoft Clarity internal error – arrives as a plain TypeError, or as an 'Error' class
      // "Uncaught (in promise): TypeError: ..." when it comes out of a promise wrapped by zone.js
      if (errorMsg.includes("Cannot read properties of null (reading 'sequence')") && hasStackSource('scripts.clarity.ms')) {
        return false;
      }

      // Android In-App Browser / WebView native bridge error
      const isNativePostMessageError = errorMsg.includes('Error invoking postMessage') && errorMsg.includes('Java exception');
      const isIabNavigationLogger = hasStackSource('navigation_performance_logger_android') || hasStackSource('iabjs://');
      if (isNativePostMessageError || isIabNavigationLogger) {
        return false;
      }

      // iOS In-App Browser (WKWebView) – host app injects a script referencing its own undefined variable
      if (errorClass === 'ReferenceError' && errorMsg.includes("Can't find variable: currentInset")) {
        return false;
      }

      // Browser/WebView-injected one-liner (stack is only "global code:1:1") calling a function it never defined
      if (errorClass === 'ReferenceError' && errorMsg.includes('runCustomize is not defined')) {
        return false;
      }

      // invalid object where a stream was expected
      // if (errorClass === 'TypeError' && errorMsg.includes('invalid object where a stream was expected')) {
      //   return false;
      // }

      // Firebase: Error (auth/network-request-failed)
      // if (errorMsg.includes('Firebase: Error (auth/network-request-failed)')) {
      //   return false;
      // }
      return true;
    }
  });
}

// create a factory which will return the Bugsnag error handler
export function errorHandlerFactory() {
  return environment.production ? new BugsnagErrorHandler() : null;
}

@NgModule({
  providers: environment.production ? [{ provide: ErrorHandler, useFactory: errorHandlerFactory }] : []
})

export class ErrorHandlerModule { }
